import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import { T, useGT } from 'gt-react';
import type { PdfTemplateResponse } from '@erp/rpc';
import type {
  PdfPageSetup,
  PdfPageSize,
  PdfPageOrientation,
} from '@erp/shared-types';
import { orpc } from '@/api/rpc/client';
import { useAgentModule } from '@/hooks/useAgentModule';
import { useAgentState } from '@/context/agent-state.context';
import type { Message, MessageToolResult } from '@/context/agent-state.context';
import {
  useCreatePdfTemplate,
  useUpdatePdfTemplate,
  useCreatePdfTemplateDraft,
  useUpdatePdfTemplateDraft,
  useFinalizePdfTemplateDraft,
  usePublishPdfTemplateVersion,
} from '@/api/rpc/hooks/communications/usePdfTemplates';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  PlusIcon,
  TrashIcon,
  RocketLaunchIcon,
  CheckIcon,
  FilePdfIcon,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import {
  EMPTY_PDF_JSON_SPEC,
  applyPageSetupToSpec,
  parsePdfSpec,
  parseJsonRecord,
  stringifyJsonSpec,
} from './pdfTemplateJsonSpec';
import {
  DEFAULT_SAMPLE_VARIABLES,
  PdfJsonTemplateEditor,
} from './PdfJsonTemplateEditor';
import { useToast } from '@/context/ToastContext';

interface PdfTemplateFormProps {
  template?: PdfTemplateResponse;
  draftVersionId?: string | null;
  onCancel: () => void;
  onSaved: () => void;
  expandedJsonEditor?: boolean;
  readOnly?: boolean;
}

interface VariableEntry {
  key: string;
  label: string;
}

const PAGE_SIZES: PdfPageSize[] = [
  'A4',
  'A3',
  'A5',
  'LETTER',
  'LEGAL',
  'TABLOID',
];
const PAGE_ORIENTATIONS: PdfPageOrientation[] = ['portrait', 'landscape'];

function parseMargin(value: string): number | undefined {
  if (!value.trim()) return undefined;
  const parsed = Number(value);
  // Negative margins are not meaningful (they'd push content off the page), so
  // ignore them and fall back to the renderer's default.
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
}

// Tool results arrive untyped from the agent stream — validate at runtime
// instead of trusting `as` casts.
const PdfProposalToolResultSchema = z.object({
  ok: z.literal(true),
  jsonSpec: z.record(z.string(), z.unknown()),
  sampleVariables: z.record(z.string(), z.unknown()),
  pageSetup: z
    .object({
      size: z.enum(['A4', 'A3', 'A5', 'LETTER', 'LEGAL', 'TABLOID']).optional(),
      orientation: z.enum(['portrait', 'landscape']).optional(),
      marginTop: z.number().optional(),
      marginBottom: z.number().optional(),
      marginLeft: z.number().optional(),
      marginRight: z.number().optional(),
    })
    .nullish(),
  templateName: z.string().nullish(),
  summary: z.string().optional(),
});

const CreateBlockToolResultSchema = z.object({
  ok: z.literal(true),
  name: z.string().optional(),
});

export function PdfTemplateForm({
  template,
  draftVersionId,
  onCancel,
  onSaved,
  expandedJsonEditor = false,
  readOnly = false,
}: PdfTemplateFormProps) {
  const isEditMode = Boolean(template);
  const gt = useGT();
  const { showToast } = useToast();

  const [name, setName] = useState(template?.name ?? '');
  const [jsonSpec, setJsonSpec] = useState(
    stringifyJsonSpec(template?.jsonSpec),
  );
  const [sampleVariables, setSampleVariables] = useState(
    DEFAULT_SAMPLE_VARIABLES,
  );
  const [variables, setVariables] = useState<VariableEntry[]>(
    template?.variables.map((v) => ({ key: v.key, label: v.label })) ?? [],
  );
  const [pageSize, setPageSize] = useState<PdfPageSize>(
    template?.pageSetup?.size ?? 'LETTER',
  );
  const [orientation, setOrientation] = useState<PdfPageOrientation>(
    template?.pageSetup?.orientation ?? 'portrait',
  );
  const [marginTop, setMarginTop] = useState(
    template?.pageSetup?.marginTop != null
      ? String(template.pageSetup.marginTop)
      : '',
  );
  const [marginBottom, setMarginBottom] = useState(
    template?.pageSetup?.marginBottom != null
      ? String(template.pageSetup.marginBottom)
      : '',
  );
  const [marginLeft, setMarginLeft] = useState(
    template?.pageSetup?.marginLeft != null
      ? String(template.pageSetup.marginLeft)
      : '',
  );
  const [marginRight, setMarginRight] = useState(
    template?.pageSetup?.marginRight != null
      ? String(template.pageSetup.marginRight)
      : '',
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [newVarKey, setNewVarKey] = useState('');
  const [newVarLabel, setNewVarLabel] = useState('');

  const createTemplate = useCreatePdfTemplate();
  const updateTemplate = useUpdatePdfTemplate();
  const createDraft = useCreatePdfTemplateDraft();
  const updateDraft = useUpdatePdfTemplateDraft();
  const finalizeDraft = useFinalizePdfTemplateDraft();
  const publishVersion = usePublishPdfTemplateVersion();

  function resetForm() {
    setName('');
    setJsonSpec(EMPTY_PDF_JSON_SPEC);
    setSampleVariables(DEFAULT_SAMPLE_VARIABLES);
    setVariables([]);
    setPageSize('LETTER');
    setOrientation('portrait');
    setMarginTop('');
    setMarginBottom('');
    setMarginLeft('');
    setMarginRight('');
    setErrors({});
    setNewVarKey('');
    setNewVarLabel('');
  }

  function addVariable() {
    const key = newVarKey.trim();
    const label = newVarLabel.trim() || key;
    if (!key) return;
    if (!/^\w+$/.test(key)) {
      setErrors((prev) => ({
        ...prev,
        variable: gt('Key must be alphanumeric with underscores only', {
          context: 'pdf template variable key validation error',
        }),
      }));
      return;
    }
    if (variables.some((v) => v.key === key)) {
      setErrors((prev) => ({
        ...prev,
        variable: gt('Variable key already exists', {
          context: 'pdf template variable key duplicate error',
        }),
      }));
      return;
    }
    setVariables((prev) => [...prev, { key, label }]);
    setNewVarKey('');
    setNewVarLabel('');
    setErrors((prev) => {
      const { variable: _, ...rest } = prev;
      return rest;
    });
  }

  function removeVariable(key: string) {
    setVariables((prev) => prev.filter((v) => v.key !== key));
  }

  const pageSetupValue = useMemo<PdfPageSetup>(
    () => ({
      size: pageSize,
      orientation,
      marginTop: parseMargin(marginTop),
      marginBottom: parseMargin(marginBottom),
      marginLeft: parseMargin(marginLeft),
      marginRight: parseMargin(marginRight),
    }),
    [pageSize, orientation, marginTop, marginBottom, marginLeft, marginRight],
  );

  // Refs read inside the (stable) agent listener so it always sees the latest
  // values without re-registering on every keystroke.
  const readOnlyRef = useRef(readOnly);
  readOnlyRef.current = readOnly;
  const nameRef = useRef(name);
  nameRef.current = name;

  const { registerToolResultListener } = useAgentState();
  const queryClient = useQueryClient();

  // Auto-apply proposals: when Nami calls propose_pdf_template_spec, write the
  // spec / sample variables / page setup / name into the editor. The editor's
  // debounced preview re-renders automatically.
  // (Unlike the email builder we skip progressive token-streaming here — each
  // PDF preview is a server render, so we apply only the final proposal.)
  useEffect(() => {
    return registerToolResultListener((_toolCallId, toolName, result) => {
      // Blocks are created server-side, bypassing the mutation hooks that
      // normally invalidate caches — refresh so the blocks pages aren't stale.
      if (toolName === 'create_template_block') {
        queryClient.invalidateQueries({
          queryKey: orpc.communications.templateBlocks.list.queryKey({
            input: {},
          }),
        });
        queryClient.invalidateQueries({
          queryKey: orpc.communications.templateBlocks.listVersions.key(),
        });
        return;
      }

      if (toolName !== 'propose_pdf_template_spec') return;
      if (readOnlyRef.current) return;
      const parsed = PdfProposalToolResultSchema.safeParse(result);
      if (!parsed.success) return;
      const r = parsed.data;
      setJsonSpec(JSON.stringify(r.jsonSpec, null, 2));
      setSampleVariables(JSON.stringify(r.sampleVariables, null, 2));
      if (r.pageSetup) {
        if (r.pageSetup.size) setPageSize(r.pageSetup.size);
        if (r.pageSetup.orientation) setOrientation(r.pageSetup.orientation);
        setMarginTop(
          r.pageSetup.marginTop != null ? String(r.pageSetup.marginTop) : '',
        );
        setMarginBottom(
          r.pageSetup.marginBottom != null
            ? String(r.pageSetup.marginBottom)
            : '',
        );
        setMarginLeft(
          r.pageSetup.marginLeft != null ? String(r.pageSetup.marginLeft) : '',
        );
        setMarginRight(
          r.pageSetup.marginRight != null
            ? String(r.pageSetup.marginRight)
            : '',
        );
      }
      // Only set the name when it's still blank, so we never clobber a
      // user-entered or existing template name.
      if (
        typeof r.templateName === 'string' &&
        r.templateName.trim() &&
        !nameRef.current.trim()
      ) {
        setName(r.templateName);
      }
    });
  }, [queryClient, registerToolResultListener]);

  const renderToolResult = useCallback(
    (_msg: Message, toolResult: MessageToolResult) => {
      if (toolResult.toolName === 'create_template_block') {
        const parsed = CreateBlockToolResultSchema.safeParse(toolResult.result);
        if (!parsed.success) return null;
        return (
          <div className="flex items-center gap-1.5 text-xs text-emerald-600">
            <CheckIcon className="h-3 w-3 flex-shrink-0" />
            <span>
              {parsed.data.name
                ? gt('Created block "{name}".', { name: parsed.data.name })
                : gt('Created block.')}
            </span>
          </div>
        );
      }
      if (toolResult.toolName !== 'propose_pdf_template_spec') return null;
      const parsed = PdfProposalToolResultSchema.safeParse(toolResult.result);
      if (!parsed.success) return null;
      return (
        <div className="flex items-center gap-1.5 text-xs text-emerald-600">
          <CheckIcon className="h-3 w-3 flex-shrink-0" />
          <span>{parsed.data.summary ?? gt('Spec applied.')}</span>
        </div>
      );
    },
    [gt],
  );

  useAgentModule({
    moduleKey: 'pdf-templates',
    currentPage: name.trim() || 'PDF Template',
    getModuleContext: () => {
      const specResult = parsePdfSpec(jsonSpec);
      const variablesResult = parseJsonRecord(sampleVariables);
      return {
        templateName: name || null,
        jsonSpec: specResult.data ?? null,
        specError: specResult.error ?? null,
        pageSetup: pageSetupValue,
        sampleVariables: variablesResult.data ?? null,
      };
    },
    renderToolResult,
    placeholder: 'Describe the PDF template you want to build…',
    headerTitle: 'PDF Builder',
    headerIcon: (
      <FilePdfIcon size={18} weight="duotone" className="text-rose-500" />
    ),
  });

  function validateForm(
    parsedJsonSpec: Record<string, unknown> | null,
  ): boolean {
    const newErrors: Record<string, string> = {};
    if (!name.trim())
      newErrors.name = gt('Name is required', {
        context: 'pdf template form validation',
      });
    const parsed = parsePdfSpec(jsonSpec);
    if (!jsonSpec.trim()) {
      newErrors.jsonSpec = gt('Document spec is required', {
        context: 'pdf template form validation',
      });
    } else if (parsed.error || !parsedJsonSpec) {
      newErrors.jsonSpec = parsed.error ?? gt('Invalid document spec.');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function handleClose() {
    resetForm();
    onCancel();
  }

  function handleSubmit() {
    const parsed = parsePdfSpec(jsonSpec).data;
    if (!validateForm(parsed) || !parsed) return;
    const parsedJsonSpec = applyPageSetupToSpec(parsed, pageSetupValue);

    const onSuccess = () => {
      resetForm();
      onSaved();
    };
    const onError = (error: Error) => {
      setErrors({
        form:
          error.message ||
          gt('Failed to save template', {
            context: 'pdf template form error',
          }),
      });
    };

    if (isEditMode && template) {
      updateTemplate.mutate(
        {
          id: template.id,
          name: name.trim(),
          jsonSpec: parsedJsonSpec,
          variables,
          pageSetup: pageSetupValue,
        },
        { onSuccess, onError },
      );
    } else {
      createTemplate.mutate(
        {
          name: name.trim(),
          jsonSpec: parsedJsonSpec,
          variables,
          pageSetup: pageSetupValue,
        },
        { onSuccess, onError },
      );
    }
  }

  async function handleSaveDraft() {
    const parsed = parsePdfSpec(jsonSpec).data;
    if (!validateForm(parsed) || !template || !parsed) return;
    const parsedJsonSpec = applyPageSetupToSpec(parsed, pageSetupValue);
    try {
      if (draftVersionId) {
        await updateDraft.mutateAsync({
          versionId: draftVersionId,
          jsonSpec: parsedJsonSpec,
          variables,
          pageSetup: pageSetupValue,
        });
      } else {
        await createDraft.mutateAsync({
          pdfTemplateId: template.id,
          jsonSpec: parsedJsonSpec,
          variables,
          pageSetup: pageSetupValue,
        });
      }
      showToast({
        title: gt('Draft saved'),
        message: gt('Your changes have been saved as a draft.'),
        color: 'success',
      });
      onSaved();
    } catch (err) {
      setErrors({ form: err instanceof Error ? err.message : String(err) });
    }
  }

  async function handleSaveAndPublish() {
    const parsed = parsePdfSpec(jsonSpec).data;
    if (!validateForm(parsed) || !template || !parsed) return;
    const parsedJsonSpec = applyPageSetupToSpec(parsed, pageSetupValue);
    try {
      let versionId: string;
      if (draftVersionId) {
        await updateDraft.mutateAsync({
          versionId: draftVersionId,
          jsonSpec: parsedJsonSpec,
          variables,
          pageSetup: pageSetupValue,
        });
        versionId = draftVersionId;
      } else {
        const { version } = await createDraft.mutateAsync({
          pdfTemplateId: template.id,
          jsonSpec: parsedJsonSpec,
          variables,
          pageSetup: pageSetupValue,
        });
        versionId = version.id;
      }
      const { version: finalized } = await finalizeDraft.mutateAsync({
        versionId,
      });
      await publishVersion.mutateAsync({
        pdfTemplateId: template.id,
        versionId: finalized.id,
      });
      showToast({
        title: gt('Published'),
        message: gt('Your changes are now live.'),
        color: 'success',
      });
      resetForm();
      onSaved();
    } catch (err) {
      setErrors({ form: err instanceof Error ? err.message : String(err) });
    }
  }

  const isPending =
    createTemplate.isPending ||
    updateTemplate.isPending ||
    createDraft.isPending ||
    updateDraft.isPending ||
    finalizeDraft.isPending ||
    publishVersion.isPending;

  return (
    <>
      <div className="space-y-4 py-4">
        {errors.form && (
          <p className="text-sm text-destructive">{errors.form}</p>
        )}

        <div className="border-b pb-4">
          <Input
            aria-label={gt('Template name', {
              context: 'pdf template inline title input aria label',
            })}
            placeholder={gt('Untitled PDF template', {
              context: 'pdf template inline title placeholder',
            })}
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={readOnly}
            className="h-auto border-transparent bg-transparent px-0 py-1 text-2xl font-semibold tracking-tight shadow-none focus-visible:ring-0 md:text-3xl"
          />
          {errors.name ? (
            <p className="text-sm text-destructive">{errors.name}</p>
          ) : (
            <p className="text-xs text-muted-foreground">
              <T>A unique identifier used to reference this template in code</T>
            </p>
          )}
        </div>

        <PdfJsonTemplateEditor
          jsonSpec={jsonSpec}
          onJsonSpecChange={readOnly ? () => {} : setJsonSpec}
          sampleVariables={sampleVariables}
          onSampleVariablesChange={readOnly ? () => {} : setSampleVariables}
          pageSetup={pageSetupValue}
          readOnly={readOnly}
          expanded={expandedJsonEditor}
          configSlot={
            <div className="space-y-4">
              <div className="space-y-3">
                <Label>
                  <T>Page Setup</T>
                </Label>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="pdfPageSize" className="text-xs">
                      <T>Size</T>
                    </Label>
                    <Select
                      value={pageSize}
                      onValueChange={(value) =>
                        setPageSize(value as PdfPageSize)
                      }
                      disabled={readOnly}
                    >
                      <SelectTrigger id="pdfPageSize" className="h-8 w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PAGE_SIZES.map((size) => (
                          <SelectItem key={size} value={size}>
                            {size}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="pdfOrientation" className="text-xs">
                      <T>Orientation</T>
                    </Label>
                    <Select
                      value={orientation}
                      onValueChange={(value) =>
                        setOrientation(value as PdfPageOrientation)
                      }
                      disabled={readOnly}
                    >
                      <SelectTrigger id="pdfOrientation" className="h-8 w-36">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PAGE_ORIENTATIONS.map((value) => (
                          <SelectItem key={value} value={value}>
                            {value === 'portrait' ? (
                              <T context="pdf page orientation">Portrait</T>
                            ) : (
                              <T context="pdf page orientation">Landscape</T>
                            )}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <MarginInput
                    id="pdfMarginTop"
                    label={gt('Top')}
                    value={marginTop}
                    onChange={setMarginTop}
                    disabled={readOnly}
                  />
                  <MarginInput
                    id="pdfMarginBottom"
                    label={gt('Bottom')}
                    value={marginBottom}
                    onChange={setMarginBottom}
                    disabled={readOnly}
                  />
                  <MarginInput
                    id="pdfMarginLeft"
                    label={gt('Left')}
                    value={marginLeft}
                    onChange={setMarginLeft}
                    disabled={readOnly}
                  />
                  <MarginInput
                    id="pdfMarginRight"
                    label={gt('Right')}
                    value={marginRight}
                    onChange={setMarginRight}
                    disabled={readOnly}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  <T>
                    Margins are measured in points. Leave blank to use the 40pt
                    default.
                  </T>
                </p>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>
                    <T>Template Variables</T>
                  </Label>
                  <span className="text-xs text-muted-foreground">
                    <T context="instruction to use template variable syntax in pdf spec">
                      Reference variables via $state in the document spec
                    </T>
                  </span>
                </div>

                {variables.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {variables.map((v) => (
                      <Badge
                        key={v.key}
                        variant="secondary"
                        className="gap-1.5 pl-2.5 pr-1 py-1"
                      >
                        <span className="flex items-center gap-1.5">
                          <code className="text-xs font-mono">{v.key}</code>
                          {v.label !== v.key && (
                            <span className="text-xs text-muted-foreground">
                              {v.label}
                            </span>
                          )}
                        </span>
                        {!readOnly && (
                          <button
                            type="button"
                            onClick={() => removeVariable(v.key)}
                            aria-label={gt('Remove variable "{key}"', {
                              key: v.key,
                              context:
                                'pdf template remove variable button aria label',
                            })}
                            className="ml-1 rounded-sm p-0.5 hover:bg-destructive/20 hover:text-destructive"
                          >
                            <TrashIcon className="h-3 w-3" />
                          </button>
                        )}
                      </Badge>
                    ))}
                  </div>
                )}

                {!readOnly && (
                  <div className="flex items-end gap-2">
                    <div className="flex-1 space-y-1">
                      <Label htmlFor="newPdfVarKey" className="text-xs">
                        <T>Key</T>
                      </Label>
                      <Input
                        id="newPdfVarKey"
                        placeholder={gt('customer_name', {
                          context: 'variable key input placeholder example',
                        })}
                        value={newVarKey}
                        onChange={(e) => setNewVarKey(e.target.value)}
                        className="h-8 text-sm"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addVariable();
                          }
                        }}
                      />
                    </div>
                    <div className="flex-1 space-y-1">
                      <Label htmlFor="newPdfVarLabel" className="text-xs">
                        <T>Label (optional)</T>
                      </Label>
                      <Input
                        id="newPdfVarLabel"
                        placeholder={gt('Customer Name', {
                          context: 'variable label input placeholder example',
                        })}
                        value={newVarLabel}
                        onChange={(e) => setNewVarLabel(e.target.value)}
                        className="h-8 text-sm"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addVariable();
                          }
                        }}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8"
                      onClick={addVariable}
                      disabled={!newVarKey.trim()}
                      aria-label={gt('Add variable', {
                        context: 'pdf template add variable button aria label',
                      })}
                    >
                      <PlusIcon className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
                {errors.variable && (
                  <p className="text-sm text-destructive">{errors.variable}</p>
                )}
              </div>
            </div>
          }
        />
        {errors.jsonSpec && (
          <p className="text-sm text-destructive">{errors.jsonSpec}</p>
        )}
      </div>

      {!readOnly && (
        <div className="flex justify-end gap-2 border-t pt-4">
          <Button variant="outline" onClick={handleClose}>
            <T>Cancel</T>
          </Button>
          {isEditMode && template ? (
            <>
              <Button
                variant="outline"
                onClick={handleSaveDraft}
                disabled={isPending}
              >
                {createDraft.isPending || updateDraft.isPending ? (
                  <T>Saving...</T>
                ) : (
                  <T>Save Draft</T>
                )}
              </Button>
              <Button onClick={handleSaveAndPublish} disabled={isPending}>
                <RocketLaunchIcon className="mr-1.5 h-4 w-4" />
                {finalizeDraft.isPending || publishVersion.isPending ? (
                  <T>Publishing...</T>
                ) : (
                  <T>Save & Publish</T>
                )}
              </Button>
            </>
          ) : (
            <Button onClick={handleSubmit} disabled={isPending}>
              {isPending ? <T>Creating...</T> : <T>Create</T>}
            </Button>
          )}
        </div>
      )}
    </>
  );
}

function MarginInput({
  id,
  label,
  value,
  onChange,
  disabled,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="text-xs">
        {label}
      </Label>
      <Input
        id={id}
        type="number"
        inputMode="numeric"
        min={0}
        value={value}
        placeholder="40"
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={cn('h-8 w-20 text-sm')}
      />
    </div>
  );
}
